import {
  customAlertApiRef,
  useDrawersContext,
  WbDrawer,
  WbDrawerContent,
  WbTable,
} from '@agilelab/plugin-wb-platform';
import { useApolloClient } from '@apollo/client';
import { useApi } from '@backstage/core-plugin-api';
import { TableBody, Typography } from '@material-ui/core';
import { FC, useEffect, useMemo, useState } from 'react';
import useAsyncFn from 'react-use/lib/useAsyncFn';
import {
  GOVERNANCE_ENTITIES,
  GovernanceEntitiesQueryType,
} from '../../../graphql/governanceEntities';
import { DagStatus } from '@agilelab/plugin-wb-builder-common';
import { Test } from '../../EditorPage/types';
import { useReleaseDetailPageContext } from '../context/useReleaseDetailPageContext';
import { getErrorMessage } from '../utils';
import { ValidationTestErrorHeader } from './ValidationTestErrorHeader';
import { ValidationTestRow } from './ValidationTestRow';

export const ValidationTestDrawer: FC = () => {
  const PAGE_SIZE_OPTIONS = [25, 50, 100];
  const DEFAULT_PAGE_SIZE = 25;

  const client = useApolloClient();
  const { toggleBoolean, drawers } = useDrawersContext();
  const {
    release,
    validationTestResultDetailContent,
    isValidationTestDrawerOpen,
    setIsValidationTestDrawerOpen,
    queryParamVersion,
    tests,
    fetchTests,
  } = useReleaseDetailPageContext();
  const alertApi = useApi(customAlertApiRef);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [asyncGovernanceEntities, fetchAsyncGovernanceEntities] =
    useAsyncFn(async () => {
      try {
        if (tests.loading || !tests.value) return undefined;

        const queryResponse = await client.query<GovernanceEntitiesQueryType>({
          query: GOVERNANCE_ENTITIES,
          variables: {
            ids: (tests.value.test?.tasks || [])
              .filter(t => !!t.governanceEntityId)
              .map(p => p.governanceEntityId),
          },
        });

        return { governanceEntities: queryResponse.data };
      } catch (error) {
        alertApi.post({ error, severity: 'error' });
        return undefined;
      }
    }, [tests]);

  useEffect(() => {
    if (isValidationTestDrawerOpen) {
      fetchAsyncGovernanceEntities();
      setPage(0);
      setPageSize(DEFAULT_PAGE_SIZE);
    }
  }, [fetchAsyncGovernanceEntities, fetchTests, isValidationTestDrawerOpen]);

  useEffect(() => {
    if (!drawers.get(0)) {
      setIsValidationTestDrawerOpen(false);
    }
  }, [drawers, setIsValidationTestDrawerOpen]);

  const errorMessage = useMemo(() => {
    const test = tests?.value?.test;
    return test && getErrorMessage(test);
  }, [tests?.value?.test]);

  const allTasks = useMemo(() => {
    const statusGravity = new Map([
      [DagStatus.FAILED, 10],
      [DagStatus.ERROR, 10],
      [DagStatus.WARNING, 7],
      [DagStatus.INFO, 6],
      [DagStatus.NOT_BLOCKING_ERROR, 5],
    ]);
    return [...(tests.value?.test?.tasks || [])].sort(
      (a, b) =>
        (statusGravity.get(b.status) ?? 0) - (statusGravity.get(a.status) ?? 0),
    );
  }, [tests.value?.test?.tasks]);
  const paginatedTasks = allTasks.slice(pageSize * page, pageSize * (page + 1));

  return (
    <>
      <WbDrawer
        open={drawers.get(0) ?? false}
        setOpen={() => toggleBoolean(0)}
        title="Validation Test"
      >
        <WbDrawerContent>
          <Typography
            style={{
              fontWeight: 'normal',
              marginBottom: errorMessage ? 0 : 16,
            }}
            color="primary"
            variant="h6"
          >
            <b>{queryParamVersion || release.metadata.version}</b> of{' '}
            {release.metadata.projectName ?? release.metadata.dataProductName}
          </Typography>

          {errorMessage && (
            <ValidationTestErrorHeader errorMessage={errorMessage} />
          )}

          <WbTable<Test>
            stickyHeader
            components={{
              tableLoader: {
                loading: asyncGovernanceEntities.loading,
              },
              tableContent: {
                body: (
                  <TableBody>
                    {paginatedTasks.map((task, i) => {
                      return (
                        <ValidationTestRow
                          key={i}
                          task={task}
                          governanceEntities={
                            asyncGovernanceEntities.value?.governanceEntities
                              ?.cgp_governance_entity || []
                          }
                        />
                      );
                    })}
                  </TableBody>
                ),
              },
            }}
            pagination={{
              count: allTasks.length,
              limit: pageSize,
              offset: pageSize * page,
              onPageChange: (newPage: number) => setPage(newPage),
              onRowsPerPageChange: (newPageSize: number) => {
                setPageSize(newPageSize);
                setPage(0);
              },
              rowsPerPageOptions: PAGE_SIZE_OPTIONS,
            }}
          />
        </WbDrawerContent>
      </WbDrawer>

      <WbDrawer
        open={drawers.get(1) ?? false}
        setOpen={() => toggleBoolean(1)}
        title="Result Details"
      >
        <WbDrawerContent>{validationTestResultDetailContent}</WbDrawerContent>
      </WbDrawer>
    </>
  );
};
